import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { IoArrowBack, IoAddCircleOutline, IoTrashOutline, IoSaveOutline, IoClose, IoSettingsOutline } from 'react-icons/io5';
import * as XLSX from 'xlsx';
import './ConfiguracionCondominioScreen.css';

const ConfiguracionCondominioScreen = () => {
    const navigate = useNavigate();
    const { currentUser, userData } = useAuth();

    const [loading, setLoading] = useState(true);
    const [hasPermission, setHasPermission] = useState(false);

    // Tabs
    const [activeTab, setActiveTab] = useState('aprobaciones'); // aprobaciones | unidades

    // Config State - Aprobaciones
    const [approvalEnabled, setApprovalEnabled] = useState(false);
    const [levels, setLevels] = useState([]); // Array of { level, threshold, approvers: [] }

    // Config State - Unidades
    const [unidades, setUnidades] = useState([]);
    const [newUnidad, setNewUnidad] = useState('');

    // Condo ID
    const [condoIdState, setCondoIdState] = useState('');

    // Config State - Warnings & Fines
    const [warningCategories, setWarningCategories] = useState('Ruido, Cochera Obstruida, Pasto Largo, Basura, Mascotas');
    const [warningsThreshold, setWarningsThreshold] = useState('3');
    const [warningsTimeframeDays, setWarningsTimeframeDays] = useState('30');
    const [autoFineAmount, setAutoFineAmount] = useState('500');

    // Config State - Convenios
    const [activeConvenioAccess, setActiveConvenioAccess] = useState('completo'); // completo | parcial | ninguno
    const [firmaComiteBase64, setFirmaComiteBase64] = useState('');
    const [isDrawing, setIsDrawing] = useState(false);
    const canvasRef = useRef(null);

    // Lists
    const [availableUsers, setAvailableUsers] = useState([]); // Potential approvers

    // Access Control Config States
    const [carriles, setCarriles] = useState([]);
    const [loadingCarriles, setLoadingCarriles] = useState(false);

    useEffect(() => {
        if (!condoIdState) return;

        setLoadingCarriles(true);
        const carrilesColRef = collection(db, 'condominios', condoIdState, 'carriles');
        const unsub = onSnapshot(carrilesColRef, (snap) => {
            const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            list.sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true, sensitivity: 'base' }));
            setCarriles(list);
            setLoadingCarriles(false);
        }, (err) => {
            console.error("Error loading carriles:", err);
            setLoadingCarriles(false);
        });

        return () => unsub();
    }, [condoIdState]);

    const handleChangeLaneMode = async (carrilId, newMode) => {
        try {
            const laneRef = doc(db, 'condominios', condoIdState, 'carriles', carrilId);
            await updateDoc(laneRef, { modoActivo: newMode });
            triggerAlert("Modo de Operación Actualizado", `El carril se ha configurado en modo: ${newMode}`);
        } catch (error) {
            console.error("Error updating lane mode:", error);
            triggerAlert("Error", "No se pudo actualizar el modo del carril: " + error.message);
        }
    };

    // Custom Modal Dialog States
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        title: '',
        message: '',
        onConfirm: null
    });
    const [alertModal, setAlertModal] = useState({
        show: false,
        title: '',
        message: ''
    });

    const triggerConfirm = (title, message, onConfirm) => {
        setConfirmModal({ show: true, title, message, onConfirm });
    };

    const triggerAlert = (title, message) => {
        setAlertModal({ show: true, title, message });
    };

    useEffect(() => {
        if (currentUser && userData) {
            checkPermissionAndLoadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, userData]);

    const checkPermissionAndLoadData = async () => {
        try {
            if (!currentUser || !userData) return;

            const role = userData.rol;

            // STRICT PERMISSION CHECK
            if (role !== 'comite' && role !== 'superuser' && role !== 'masteruser' && role !== 'admin') {
                alert("Acceso Denegado. Solo miembros del Comité y Administradores pueden modificar la configuración.");
                navigate('/');
                return;
            }
            setHasPermission(true);

            // 2. Load Condo Settings
            const targetCondoId = userData.condominioId;
            if (!targetCondoId || targetCondoId === 'GLOBAL') {
                setLoading(false);
                setHasPermission(false);
                alert("Para ver la configuración de condominio, debes entrar a un condominio específico o simular a su administrador.");
                navigate('/');
                return;
            }
            setCondoIdState(targetCondoId);

            const condoRef = doc(db, "condominios", targetCondoId);
            const condoSnap = await getDoc(condoRef);

            if (condoSnap.exists()) {
                const data = condoSnap.data();
                if (data.approvalRules) {
                    setApprovalEnabled(data.approvalRules.enabled || false);
                    setLevels(data.approvalRules.levels || []);
                }
                if (data.unidades) {
                    setUnidades(data.unidades);
                }
                if (data.warningsConfig) {
                    setWarningCategories(data.warningsConfig.categories?.join(', ') || 'Ruido, Cochera Obstruida, Pasto Largo, Basura, Mascotas');
                    setWarningsThreshold(String(data.warningsConfig.threshold || '3'));
                    setWarningsTimeframeDays(String(data.warningsConfig.timeframeDays || '30'));
                    setAutoFineAmount(String(data.warningsConfig.autoFineAmount || '500'));
                }
                if (data.conveniosConfig) {
                    setActiveConvenioAccess(data.conveniosConfig.activeAccess || 'completo');
                    setFirmaComiteBase64(data.conveniosConfig.firmaComiteBase64 || '');
                }
            }

            // 3. Load Potential Approvers
            const usersQ = query(
                collection(db, "usuarios"),
                where("condominioId", "==", targetCondoId),
                where("rol", "in", ["admin", "comite", "residente"])
            );
            const usersSnap = await getDocs(usersQ);
            const usersList = usersSnap.docs.map(d => ({
                id: d.id,
                ...d.data()
            }));
            setAvailableUsers(usersList);

            setLoading(false);

        } catch (error) {
            console.error(error);
            alert("Error cargando configuración: " + error.message);
        }
    };

    const addLevel = () => {
        const newLevel = {
            levelId: levels.length + 1,
            threshold: '',
            approvers: []
        };
        setLevels([...levels, newLevel]);
    };

    const removeLevel = (index) => {
        const newLevels = [...levels];
        newLevels.splice(index, 1);
        setLevels(newLevels.map((l, i) => ({ ...l, levelId: i + 1 })));
    };

    const updateLevel = (index, field, value) => {
        const newLevels = [...levels];
        newLevels[index][field] = value;
        setLevels(newLevels);
    };

    const toggleApprover = (levelIndex, userId) => {
        const newLevels = [...levels];
        const currentApprovers = newLevels[levelIndex].approvers || [];

        if (currentApprovers.includes(userId)) {
            newLevels[levelIndex].approvers = currentApprovers.filter(id => id !== userId);
        } else {
            newLevels[levelIndex].approvers = [...currentApprovers, userId];
        }
        setLevels(newLevels);
    };

    const handleExcelUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);
                
                const nuevasUnidades = [];
                const saldosInicialesMap = []; // Array of { unidad, saldo }

                data.forEach(row => {
                    const val = row['Unidad'] || row['unidad'] || row['UNIDAD'] || row['Casa'] || row['casa'] || Object.values(row)[0];
                    const unidadStr = val !== undefined && val !== null ? String(val).trim() : null;
                    if (unidadStr && unidadStr !== '') {
                        nuevasUnidades.push(unidadStr);
                        
                        // Parse Saldo/Saldo Inicial column
                        const saldoVal = row['Saldo'] || row['saldo'] || row['SALDO'] || row['Saldo Inicial'] || row['saldo inicial'];
                        if (saldoVal !== undefined && saldoVal !== null && !isNaN(parseFloat(saldoVal))) {
                            saldosInicialesMap.push({
                                unidad: unidadStr,
                                saldo: parseFloat(saldoVal)
                            });
                        }
                    }
                });

                if (nuevasUnidades.length === 0) {
                    triggerAlert("Importación Excel", "No se encontraron unidades en el Excel. Asegúrate de que haya una columna llamada 'Unidad'.");
                    return;
                }

                const applyUnidades = () => {
                    const uniqueSet = new Set([...unidades, ...nuevasUnidades]);
                    setUnidades(Array.from(uniqueSet).sort((a,b) => a.localeCompare(b, undefined, {numeric: true})));
                    triggerAlert("Importación Exitosa", `Se importaron ${nuevasUnidades.length} unidades correctamente.`);
                };

                // Process initial balances if present
                if (saldosInicialesMap.length > 0) {
                    triggerConfirm(
                        "Saldos Iniciales Detectados",
                        `Se detectaron ${saldosInicialesMap.length} saldos iniciales. ¿Deseas importarlos? (Los saldos positivos crearán cargos de adeudo; los saldos negativos se registrarán como saldo a favor si hay un residente asignado, o se guardarán para cuando se registre).`,
                        async () => {
                            setLoading(true);
                            try {
                                const targetCondoId = condoIdState;
                                if (targetCondoId) {
                                    const { collection, addDoc, getDocs, query, where, doc, updateDoc, increment } = require('firebase/firestore');
                                    
                                    for (const item of saldosInicialesMap) {
                                        if (item.saldo > 0) {
                                            // Debtor: Create a cargo
                                            await addDoc(collection(db, "cargos"), {
                                                condominioId: targetCondoId,
                                                unidadResidente: item.unidad,
                                                concepto: "Saldo Inicial",
                                                monto: item.saldo,
                                                montoPagado: 0,
                                                estatus: "pendiente",
                                                fechaVencimiento: new Date(),
                                                fechaCreacion: new Date(),
                                                tipo: "Saldo Inicial"
                                            });
                                        } else if (item.saldo < 0) {
                                            // Credit: search for registered user in this unit
                                            const absVal = Math.abs(item.saldo);
                                            const uQ = query(
                                                collection(db, "usuarios"),
                                                where("condominioId", "==", targetCondoId),
                                                where("numeroCasa", "==", item.unidad)
                                            );
                                            const uSnap = await getDocs(uQ);
                                            if (!uSnap.empty) {
                                                for (const uDoc of uSnap.docs) {
                                                    await updateDoc(doc(db, "usuarios", uDoc.id), {
                                                        saldoFavor: increment(absVal)
                                                    });
                                                }
                                            } else {
                                                // Store in condo doc pendingCredits map
                                                const cRef = doc(db, "condominios", targetCondoId);
                                                const field = `pendingCredits.${item.unidad.replace(/\./g, '_')}`;
                                                await updateDoc(cRef, {
                                                    [field]: increment(absVal)
                                                });
                                            }
                                        }
                                    }
                                    triggerAlert("Saldos Importados", "Saldos iniciales procesados correctamente.");
                                }
                                applyUnidades();
                            } catch (e) {
                                console.error(e);
                                triggerAlert("Error de Importación", "Error al procesar saldos iniciales: " + e.message);
                            } finally {
                                setLoading(false);
                            }
                        }
                    );
                } else {
                    applyUnidades();
                }
            } catch (err) {
                console.error(err);
                triggerAlert("Error", "Error al procesar el archivo Excel: " + err.message);
                setLoading(false);
            }
        };
        reader.readAsBinaryString(file);
    };

    const saveSettings = async () => {
        triggerConfirm(
            "Confirmación",
            "¿Guardar cambios en la configuración del condominio?",
            async () => {
                try {
                    const condoRef = doc(db, "condominios", condoIdState);

                    // Sanitize Logic
                    const cleanLevels = levels.map(l => ({
                        levelId: l.levelId,
                        threshold: parseFloat(l.threshold) || 0,
                        approvers: l.approvers || []
                    }));

                    const updatePayload = {
                        unidades: unidades,
                        // Warnings & Fine Settings
                        warningsConfig: {
                            categories: warningCategories.split(',').map(c => c.trim()).filter(Boolean),
                            threshold: parseInt(warningsThreshold) || 3,
                            timeframeDays: parseInt(warningsTimeframeDays) || 30,
                            autoFineAmount: parseFloat(autoFineAmount) || 0
                        },
                        // Convenios Access Config
                        conveniosConfig: {
                            activeAccess: activeConvenioAccess,
                            firmaComiteBase64: firmaComiteBase64
                        }
                    };

                    const role = userData?.rol;
                    if (role === 'comite' || role === 'superuser' || role === 'masteruser') {
                        updatePayload.approvalRules = {
                            enabled: approvalEnabled,
                            levels: cleanLevels,
                            updatedBy: currentUser.uid,
                            updatedAt: new Date()
                        };
                    }

                    await updateDoc(condoRef, updatePayload);

                    triggerAlert("Configuración Guardada", "La configuración del condominio se ha guardado correctamente.");
                    setTimeout(() => {
                        navigate('/perfil');
                    }, 1500);

                } catch (error) {
                    console.error(error);
                    triggerAlert("Error", "Error al guardar: " + error.message);
                }
            }
        );
    };

    const startDrawing = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        
        const rect = canvas.getBoundingClientRect();
        const clientX = e.clientX || (e.touches && e.touches[0] && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0] && e.touches[0].clientY);
        if (clientX === undefined || clientY === undefined) return;
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        
        ctx.beginPath();
        ctx.moveTo(x, y);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        
        const rect = canvas.getBoundingClientRect();
        const clientX = e.clientX || (e.touches && e.touches[0] && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0] && e.touches[0].clientY);
        if (clientX === undefined || clientY === undefined) return;
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setFirmaComiteBase64('');
    };

    const saveCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const base64 = canvas.toDataURL('image/png');
        setFirmaComiteBase64(base64);
        triggerAlert("Firma Capturada", "Firma digitalizada guardada temporalmente. Presiona el botón de guardar configuración abajo para confirmar.");
    };

    if (loading) return <div className="loading-container">Cargando configuración...</div>;
    if (!hasPermission) return null;

    return (
        <div className="usuarios-container">
            {/* Sticky Compact Header */}
            <div className="app-header-sticky">
                <div className="kondo-banner-standard" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                    <div className="banner-standard-content">
                        <button onClick={() => navigate(-1)} className="banner-back-btn-standard">
                            <IoArrowBack size={24} />
                        </button>
                        <div className="banner-standard-text">
                            <h2>Configuración</h2>
                            <p>Condominio y Reglas</p>
                        </div>
                    </div>
                    <IoSettingsOutline className="banner-standard-icon-decor" />
                </div>
            </div>

            <div className="config-aprobaciones-body">
                {/* TABS */}
                <div className="tabs-standard-container">
                    <button 
                        onClick={() => setActiveTab('aprobaciones')} 
                        className={`tab-btn-standard ${activeTab === 'aprobaciones' ? 'active' : ''}`}
                    >
                        Flujos de Aprobación
                    </button>
                    <button 
                        onClick={() => setActiveTab('unidades')} 
                        className={`tab-btn-standard ${activeTab === 'unidades' ? 'active' : ''}`}
                    >
                        Catálogo de Unidades
                    </button>
                    <button 
                        onClick={() => setActiveTab('apercibimientos')} 
                        className={`tab-btn-standard ${activeTab === 'apercibimientos' ? 'active' : ''}`}
                    >
                        Apercibimientos
                    </button>
                    <button 
                        onClick={() => setActiveTab('convenios')} 
                        className={`tab-btn-standard ${activeTab === 'convenios' ? 'active' : ''}`}
                    >
                        Convenios
                    </button>
                    <button 
                        onClick={() => setActiveTab('carriles')} 
                        className={`tab-btn-standard ${activeTab === 'carriles' ? 'active' : ''}`}
                    >
                        Carriles Vehiculares
                    </button>
                </div>

                {activeTab === 'apercibimientos' && (
                    <div className="config-approvals-card" style={{ padding: '25px' }}>
                        <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem', color: '#111827' }}>Reglas de Apercibimientos y Multas</h3>
                        <p style={{ margin: '0 0 20px 0', color: '#4b5563', fontSize: '0.95rem', lineHeight: '1.5' }}>
                            Configura las categorías de apercibimiento válidas y las reglas para automatizar multas cuando los residentes acumulen advertencias en un rango de tiempo.
                        </p>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Categorías de Apercibimiento (separadas por comas)</label>
                            <input
                                type="text"
                                className="form-input"
                                placeholder="Ej: Ruido, Cochera Obstruida, Basura, Mascotas"
                                value={warningCategories}
                                onChange={(e) => setWarningCategories(e.target.value)}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                            <div className="form-group">
                                <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Advertencias para Multa</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder="Ej: 3"
                                    value={warningsThreshold}
                                    onChange={(e) => setWarningsThreshold(e.target.value)}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Rango de tiempo (días)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder="Ej: 30"
                                    value={warningsTimeframeDays}
                                    onChange={(e) => setWarningsTimeframeDays(e.target.value)}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Monto automático Multa (MXN)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder="Ej: 500"
                                    value={autoFineAmount}
                                    onChange={(e) => setAutoFineAmount(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'convenios' && (
                    <div className="config-approvals-card" style={{ padding: '25px' }}>
                        <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem', color: '#111827' }}>Reglas de Convenios de Pago</h3>
                        <p style={{ margin: '0 0 20px 0', color: '#4b5563', fontSize: '0.95rem', lineHeight: '1.5' }}>
                            Establece los privilegios de acceso y morosidad de los residentes mientras tengan un convenio de pago activo en el sistema.
                        </p>
                         <div className="form-group" style={{ marginBottom: '25px' }}>
                            <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Acceso a amenidades/lector RFID durante convenio activo</label>
                            <select
                                className="form-input"
                                value={activeConvenioAccess}
                                onChange={(e) => setActiveConvenioAccess(e.target.value)}
                            >
                                <option value="completo">Acceso Completo (Se considera "Al Corriente" para RFID)</option>
                                <option value="parcial">Acceso Restringido (Mantiene reglas o estatus de morosidad local)</option>
                                <option value="ninguno">Acceso Bloqueado (Permanece bloqueado hasta liquidación total)</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>Firma del Comité / Administrador (Para Convenios Digitales)</label>
                            <p style={{ margin: '0 0 15px 0', color: '#6b7280', fontSize: '0.85rem' }}>
                                Esta firma se estampará automáticamente en los convenios de pago generados en formato PDF. Dibújala en el recuadro a continuación y presiona "Capturar Firma".
                            </p>

                            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                <div style={{ flex: 1, minWidth: '280px' }}>
                                    <div style={{ border: '2px dashed #cbd5e1', borderRadius: '12px', background: '#f8fafc', padding: '5px', display: 'inline-block' }}>
                                        <canvas
                                            ref={canvasRef}
                                            width={350}
                                            height={150}
                                            onMouseDown={startDrawing}
                                            onMouseMove={draw}
                                            onMouseUp={stopDrawing}
                                            onMouseLeave={stopDrawing}
                                            onTouchStart={startDrawing}
                                            onTouchMove={draw}
                                            onTouchEnd={stopDrawing}
                                            style={{ display: 'block', background: '#ffffff', cursor: 'crosshair', touchAction: 'none' }}
                                        />
                                    </div>
                                    <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                                        <button 
                                            type="button" 
                                            onClick={clearCanvas} 
                                            className="action-btn-secondary" 
                                            style={{ padding: '8px 16px', fontSize: '0.85rem', borderColor: '#ef4444', color: '#ef4444' }}
                                        >
                                            Limpiar
                                        </button>
                                        <button 
                                            type="button" 
                                            onClick={saveCanvas} 
                                            className="action-btn-primary" 
                                            style={{ padding: '8px 16px', fontSize: '0.85rem', background: '#3b82f6' }}
                                        >
                                            Capturar Firma
                                        </button>
                                    </div>
                                </div>

                                <div style={{ flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '15px', background: '#ffffff' }}>
                                    <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#64748b', marginBottom: '10px' }}>Firma Actual Registrada</span>
                                    {firmaComiteBase64 ? (
                                        <img 
                                            src={firmaComiteBase64} 
                                            alt="Firma del Comité" 
                                            style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'contain', border: '1px solid #cbd5e1', padding: '5px', borderRadius: '4px' }} 
                                        />
                                    ) : (
                                        <div style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', padding: '20px 0' }}>Sin firma registrada</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'aprobaciones' && (
                    <>
                        <div className="config-approvals-card toggle-card">
                            <div className="toggle-info">
                                <h3>Flujo de Aprobación Activo</h3>
                                <p>Si está desactivado, todos los registros de gastos se aprobarán automáticamente sin pasar por revisiones.</p>
                            </div>
                            <label className="premium-switch">
                                <input
                                    type="checkbox"
                                    checked={approvalEnabled}
                                    onChange={(e) => setApprovalEnabled(e.target.checked)}
                                />
                                <span className="premium-slider"></span>
                            </label>
                        </div>

                        {approvalEnabled && (
                            <div className="niveles-container">
                                <div className="niveles-header">
                                    <h3>Niveles de Aprobación (Cascada)</h3>
                                    <button className="action-btn-primary" onClick={addLevel}>
                                        <IoAddCircleOutline size={18} /> Añadir Nivel
                                    </button>
                                </div>
                                <p className="niveles-desc">
                                    Define límites de monto. El sistema pedirá autorización secuencialmente (Nivel 1 -&gt; Nivel 2...) a los usuarios aquí autorizados.
                                </p>

                                <div className="niveles-grid">
                                    {levels.map((lvl, index) => (
                                        <div key={index} className="config-approvals-card level-card">
                                            <div className="level-header">
                                                <div className="level-title-badge">Nivel {lvl.levelId}</div>
                                                <button className="glass-icon-btn danger-glass" onClick={() => removeLevel(index)} title="Eliminar Nivel">
                                                    <IoTrashOutline size={18} />
                                                </button>
                                            </div>

                                            <div className="form-group" style={{ marginBottom: '20px' }}>
                                                <label className="form-label" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>A partir del Monto Mínimo (MXN)</label>
                                                <input
                                                    type="number"
                                                    className="form-input"
                                                    placeholder="Ej. 1000"
                                                    value={lvl.threshold}
                                                    onChange={(e) => updateLevel(index, 'threshold', e.target.value)}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label className="form-label" style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: '#374151' }}>Aprobadores Autorizados</label>

                                                <div className="approvers-pills-container">
                                                    {(!lvl.approvers || lvl.approvers.length === 0) && (
                                                        <div className="no-approvers">Ningún aprobador asignado a este nivel.</div>
                                                    )}
                                                    {lvl.approvers?.map(approverId => {
                                                        const user = availableUsers.find(u => u.id === approverId);
                                                        if (!user) return null;
                                                        return (
                                                            <div key={approverId} className="approver-pill">
                                                                <div className="pill-info">
                                                                    <span className="pill-name">{user.nombre}</span>
                                                                    <span className={`pill-role badge-${user.rol}`}>{user.rol}</span>
                                                                </div>
                                                                <button onClick={() => toggleApprover(index, approverId)} className="pill-remove-btn"><IoClose size={16} /></button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                <div style={{ marginTop: '10px' }}>
                                                    <select
                                                        className="form-input add-approver-select"
                                                        value=""
                                                        onChange={(e) => {
                                                            if (e.target.value) {
                                                                toggleApprover(index, e.target.value);
                                                            }
                                                        }}
                                                    >
                                                        <option value="" disabled>+ Seleccionar persona para autorizar...</option>
                                                        {availableUsers.filter(u => !lvl.approvers?.includes(u.id)).map(u => (
                                                            <option key={u.id} value={u.id}>{u.nombre} ({u.rol})</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'carriles' && (
                    <div className="config-approvals-card" style={{ padding: '25px' }}>
                        <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem', color: '#111827' }}>Carriles y Puertas Vehiculares</h3>
                        <p style={{ margin: '0 0 20px 0', color: '#4b5563', fontSize: '0.95rem', lineHeight: '1.5' }}>
                            Monitorea y configura el modo de operación de los carriles y automatizaciones (plumas y portones) de tu fraccionamiento.
                        </p>

                        {loadingCarriles ? (
                            <div style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>Cargando carriles...</div>
                        ) : carriles.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', border: '1px dashed #cbd5e1', borderRadius: '12px' }}>
                                <IoSettingsOutline size={40} style={{ opacity: 0.3, marginBottom: '10px' }} />
                                <p style={{ margin: 0, fontWeight: 'bold' }}>No se han sincronizado carriles vehiculares.</p>
                                <span style={{ fontSize: '0.85rem' }}>Pídele al Superusuario de Kondify que asigne y sincronice la infraestructura vehicular de este condominio en su CRM.</span>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                {carriles.map((c) => {
                                    const prog = c.programacionHoraria || { enabled: false, defaultMode: 'MAXIMA_SEGURIDAD', reglas: [] };
                                    const laneRef = doc(db, 'condominios', condoIdState, 'carriles', c.id);

                                    const handleToggleProg = async (e) => {
                                        await updateDoc(laneRef, {
                                            "programacionHoraria.enabled": e.target.checked
                                        });
                                    };

                                    const handleChangeDefaultMode = async (e) => {
                                        await updateDoc(laneRef, {
                                            "programacionHoraria.defaultMode": e.target.value
                                        });
                                    };

                                    const handleAddRule = async () => {
                                        const newReglas = [...(prog.reglas || [])];
                                        newReglas.push({
                                            id: Math.random().toString(36).substring(7),
                                            dias: 'semana', // semana | fin | todos
                                            horaInicio: '06:00',
                                            horaFin: '09:00',
                                            modo: 'TRANSITO_FLUIDO'
                                        });
                                        await updateDoc(laneRef, {
                                            "programacionHoraria.reglas": newReglas
                                        });
                                    };

                                    const handleDeleteRule = async (ruleId) => {
                                        const newReglas = (prog.reglas || []).filter(r => r.id !== ruleId);
                                        await updateDoc(laneRef, {
                                            "programacionHoraria.reglas": newReglas
                                        });
                                    };

                                    const handleUpdateRule = async (ruleId, field, value) => {
                                        const newReglas = (prog.reglas || []).map(r => {
                                            if (r.id === ruleId) {
                                                return { ...r, [field]: value };
                                            }
                                            return r;
                                        });
                                        await updateDoc(laneRef, {
                                            "programacionHoraria.reglas": newReglas
                                        });
                                    };

                                    return (
                                        <div key={c.id} style={{ display: 'flex', flexDirection: 'column', padding: '20px', background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', gap: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                                            {/* TOP ROW: Info and Current Mode */}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                                                <div style={{ flex: 1, minWidth: '250px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                                        <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a', fontWeight: 'bold' }}>{c.nombre}</h4>
                                                        <span style={{ background: c.tipo === 'entrada' ? '#dcfce7' : '#fee2e2', color: c.tipo === 'entrada' ? '#14532d' : '#7f1d1d', padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                                                            {c.tipo}
                                                        </span>
                                                    </div>
                                                    
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8rem', color: '#64748b' }}>
                                                        <div>Herrajes: <strong>{c.herrajes?.join(' y ') || 'Ninguno'}</strong> {c.tipoPorton && `(Portón: ${c.tipoPorton === 'abatible2' ? 'Doble Abatible' : c.tipoPorton === 'abatible1' ? '1 Hoja Abatible' : 'Corredizo'})`}</div>
                                                        <div>Lectoras RFID: <strong>Puerto {c.lectoras?.join(', ') || 'N/A'}</strong> • Relevadores: {c.relevadorPluma && <span>Pluma (Pin {c.relevadorPluma})</span>} {c.relevadorPorton && <span>| Portón (Pin {c.relevadorPorton})</span>}</div>
                                                    </div>
                                                </div>

                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '220px' }}>
                                                    <label style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#475569' }}>MODO OPERATIVO MANUAL</label>
                                                    <select
                                                        disabled={prog.enabled}
                                                        value={c.modoActivo || 'TRANSITO_FLUIDO'}
                                                        onChange={(e) => handleChangeLaneMode(c.id, e.target.value)}
                                                        style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', background: prog.enabled ? '#f1f5f9' : 'white', color: '#1e293b', fontWeight: 'bold', outline: 'none', cursor: prog.enabled ? 'not-allowed' : 'pointer' }}
                                                    >
                                                        <option value="TRANSITO_FLUIDO">Tránsito Fluido (Portón Abierto / Pluma Activa)</option>
                                                        <option value="MAXIMA_SEGURIDAD">Máxima Seguridad (Pluma Abierta / Portón Activo)</option>
                                                        <option value="EMERGENCIA">🚨 Emergencia (Todo Abierto)</option>
                                                    </select>
                                                    {prog.enabled && <span style={{ fontSize: '0.75rem', color: '#6366f1', fontWeight: '500' }}>ℹ️ Controlado por la agenda horaria</span>}
                                                </div>
                                            </div>

                                            {/* SEPARATOR */}
                                            <div style={{ height: '1px', background: '#f1f5f9' }} />

                                            {/* SCHEDULE RULES SECTION */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold', color: '#334155', fontSize: '0.9rem' }}>
                                                        <input 
                                                            type="checkbox" 
                                                            checked={prog.enabled} 
                                                            onChange={handleToggleProg} 
                                                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                                        />
                                                        Habilitar Programación Horaria Automática
                                                    </label>

                                                    {prog.enabled && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.85rem' }}>
                                                            <span style={{ color: '#64748b' }}>Fuera de horario operar en:</span>
                                                            <select
                                                                value={prog.defaultMode || 'MAXIMA_SEGURIDAD'}
                                                                onChange={handleChangeDefaultMode}
                                                                style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'white', fontWeight: '600' }}
                                                            >
                                                                <option value="TRANSITO_FLUIDO">Tránsito Fluido</option>
                                                                <option value="MAXIMA_SEGURIDAD">Máxima Seguridad</option>
                                                                <option value="EMERGENCIA">Emergencia</option>
                                                            </select>
                                                        </div>
                                                    )}
                                                </div>

                                                {prog.enabled && (
                                                    <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                        {(!prog.reglas || prog.reglas.length === 0) ? (
                                                            <div style={{ textAlign: 'center', padding: '15px', color: '#94a3b8', fontSize: '0.85rem' }}>
                                                                No hay reglas horarias creadas. El carril operará siempre en el modo fuera de horario.
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                                {(prog.reglas || []).map((r) => (
                                                                    <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'white', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', flexWrap: 'wrap' }}>
                                                                        <select
                                                                            value={r.dias || 'semana'}
                                                                            onChange={(e) => handleUpdateRule(r.id, 'dias', e.target.value)}
                                                                            style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'white', fontSize: '0.85rem' }}
                                                                        >
                                                                            <option value="semana">Lunes a Viernes</option>
                                                                            <option value="fin">Sábado y Domingo</option>
                                                                            <option value="todos">Todos los Días</option>
                                                                        </select>

                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.85rem' }}>
                                                                            <span>De:</span>
                                                                            <input 
                                                                                type="time" 
                                                                                value={r.horaInicio} 
                                                                                onChange={(e) => handleUpdateRule(r.id, 'horaInicio', e.target.value)}
                                                                                style={{ padding: '3px 6px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                                                            />
                                                                            <span>A:</span>
                                                                            <input 
                                                                                type="time" 
                                                                                value={r.horaFin} 
                                                                                onChange={(e) => handleUpdateRule(r.id, 'horaFin', e.target.value)}
                                                                                style={{ padding: '3px 6px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                                                                            />
                                                                        </div>

                                                                        <select
                                                                            value={r.modo || 'TRANSITO_FLUIDO'}
                                                                            onChange={(e) => handleUpdateRule(r.id, 'modo', e.target.value)}
                                                                            style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'white', fontSize: '0.85rem', fontWeight: 'bold' }}
                                                                        >
                                                                            <option value="TRANSITO_FLUIDO">Tránsito Fluido</option>
                                                                            <option value="MAXIMA_SEGURIDAD">Máxima Seguridad</option>
                                                                            <option value="EMERGENCIA">🚨 Emergencia</option>
                                                                        </select>

                                                                        <button 
                                                                            type="button" 
                                                                            onClick={() => handleDeleteRule(r.id)}
                                                                            style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '5px' }}
                                                                        >
                                                                            <IoTrashOutline size={18} />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        <button
                                                            type="button"
                                                            onClick={handleAddRule}
                                                            style={{ alignSelf: 'flex-start', background: '#3b82f6', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                                                        >
                                                            + Agregar Rango Horario
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'unidades' && (
                    <div className="config-approvals-card" style={{ padding: '25px' }}>
                        <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem', color: '#111827' }}>Catálogo de Unidades y Formato</h3>
                        <p style={{ margin: '0 0 20px 0', color: '#4b5563', fontSize: '0.95rem', lineHeight: '1.5' }}>
                            Define aquí las unidades (casas, lotes o departamentos) legítimas de tu condominio. 
                            <br/><strong>Importante:</strong> Al importar desde Excel, el sistema buscará la columna llamada <strong>"Unidad"</strong>.
                        </p>
                        
                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', gap: '10px', flex: 1, minWidth: '250px' }}>
                                <input 
                                    type="text" 
                                    className="form-input" 
                                    placeholder="Ej: 020, Depto 5..."
                                    value={newUnidad}
                                    style={{ flex: 1 }}
                                    onChange={(e) => setNewUnidad(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && newUnidad && !unidades.includes(newUnidad.trim())) {
                                            setUnidades([...unidades, newUnidad.trim()].sort((a,b) => a.localeCompare(b, undefined, {numeric: true})));
                                            setNewUnidad('');
                                        }
                                    }}
                                />
                                <button className="action-btn-primary" 
                                    style={{ background: '#3b82f6', boxShadow: '0 4px 10px rgba(59, 130, 246, 0.3)' }}
                                    onClick={() => {
                                    if (newUnidad && !unidades.includes(newUnidad.trim())) {
                                        setUnidades([...unidades, newUnidad.trim()].sort((a,b) => a.localeCompare(b, undefined, {numeric: true})));
                                        setNewUnidad('');
                                    }
                                }}>Añadir</button>
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: '#9ca3af', fontWeight: '500' }}>o</span>
                                <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '10px 20px', background: '#f3f4f6', borderRadius: '8px', border: '1px solid #d1d5db', color: '#374151', fontWeight: 'bold', transition: 'background 0.2s', whiteSpace: 'nowrap' }} className="hover-gray">
                                    Subir Excel
                                    <input type="file" accept=".xlsx, .xls, .csv" hidden onChange={handleExcelUpload} />
                                </label>
                            </div>
                        </div>

                        <div style={{ marginTop: '30px', background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
                            {unidades.length === 0 && <p style={{ color: '#94a3b8', margin: 0, width: '100%', textAlign: 'center', padding: '20px 0' }}>Aún no has registrado ninguna unidad en el catálogo.</p>}
                            {unidades.map((u, idx) => (
                                <div key={idx} style={{ background: 'white', color: '#0f172a', padding: '8px 16px', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '10px', border: '1px solid #cbd5e1', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', fontWeight: '500', fontSize: '0.95rem' }}>
                                    {u}
                                    <IoClose style={{ cursor: 'pointer', color: '#ef4444' }} onClick={() => setUnidades(unidades.filter(un => un !== u))} title="Eliminar unidad" size={18} />
                                </div>
                            ))}
                        </div>
                        <p style={{ margin: '15px 0 0 0', color: '#64748b', fontSize: '0.85rem' }}>Total en catálogo: <strong>{unidades.length}</strong> unidades.</p>
                    </div>
                )}

                <div className="approvals-save-action" style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                    <button className="action-btn-primary" onClick={saveSettings} style={{ padding: '14px 28px', fontSize: '1.05rem', background: '#10b981', boxShadow: '0 4px 10px rgba(16, 185, 129, 0.3)' }}>
                        <IoSaveOutline size={20} /> Guardar Configuración del Condominio
                    </button>
                    <p style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '10px' }}>Recuerda presionar este botón para que los cambios en las pestañas tengan efecto.</p>
                </div>
            </div>

            {/* Custom Confirmation Modal */}
            {confirmModal.show && (
                <div className="modal-overlay" style={{ zIndex: 9999 }}>
                    <div className="modal-content" style={{ maxWidth: '400px', borderRadius: '20px', padding: '25px', textAlign: 'center' }}>
                        <h3 style={{ margin: '0 0 15px 0', fontSize: '1.25rem', color: '#1f2937' }}>{confirmModal.title}</h3>
                        <p style={{ margin: '0 0 25px 0', color: '#4b5563', fontSize: '0.95rem', lineHeight: '1.5' }}>{confirmModal.message}</p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <button className="action-btn-secondary" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null })}>
                                Cancelar
                            </button>
                            <button className="action-btn-primary" style={{ flex: 1, justifyContent: 'center', background: '#10b981', boxShadow: '0 4px 10px rgba(16, 185, 129, 0.3)' }} onClick={() => {
                                confirmModal.onConfirm();
                                setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
                            }}>
                                Aceptar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Alert Modal */}
            {alertModal.show && (
                <div className="modal-overlay" style={{ zIndex: 9999 }}>
                    <div className="modal-content" style={{ maxWidth: '400px', borderRadius: '20px', padding: '25px', textAlign: 'center' }}>
                        <h3 style={{ margin: '0 0 15px 0', fontSize: '1.25rem', color: '#1f2937' }}>{alertModal.title}</h3>
                        <p style={{ margin: '0 0 25px 0', color: '#4b5563', fontSize: '0.95rem', lineHeight: '1.5' }}>{alertModal.message}</p>
                        <button className="action-btn-primary" style={{ width: '100%', justifyContent: 'center', background: '#6348D1' }} onClick={() => setAlertModal({ show: false, title: '', message: '' })}>
                            Aceptar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConfiguracionCondominioScreen;
